import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { Alert, Button, Card, Field, Input, PageHeader } from "@empite/ui-components";
import { useAdminSignIn } from "../admin-hooks";
import { ErrorAlert, safeReturnPath } from "./guards";

const schema = z.object({
  email: z.string().min(1, "Enter your operator email.").email("That is not an email address."),
  password: z.string().min(1, "Enter your password."),
});

type Values = z.infer<typeof schema>;

export interface SignInSearch {
  /** Where to go once signed in. A same-origin path, checked before use. */
  next?: string;
}

/**
 * Operator sign-in — email and password, and nothing else.
 *
 * No OTP and no "continue with": this realm authenticates against its own
 * admin_users table and nothing else, so a customer credential from the suite
 * portal cannot be exchanged here.
 *
 * There IS a self-service path now, and it is linked from this screen — but it
 * registers a WHOLE NEW TENANT rather than an account in an existing one. That
 * distinction is drawn on the screen because somebody who was invited into a
 * colleague's company and signs up instead ends up alone in a company of their
 * own, with no error to tell them so. A seat in an existing tenant only ever
 * arrives by invitation.
 *
 * This is also a different realm from the customer portal on 5173: a customer
 * account will not sign in here, and the Admin API records the attempt if one
 * tries.
 */
export function SignIn({ search }: { search: SignInSearch }) {
  const navigate = useNavigate();
  const signIn = useAdminSignIn();
  const [formError, setFormError] = useState<unknown>(null);
  const form = useZodForm<Values>(schema, { defaultValues: { email: "", password: "" } });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await signIn.mutateAsync(values);
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["email", "password"])) {
        setFormError(err);
      }
      return;
    }
    const next = safeReturnPath(search.next);
    if (next) {
      // A full navigation rather than a router push: `next` is an opaque
      // path with its own query string, and reloading also picks up the
      // freshly set session cookie.
      window.location.assign(next);
      return;
    }
    await navigate({ to: "/" });
  });

  return (
    <>
      <PageHeader
        title="Admin sign-in"
        description="The back office for the Empite Suite. Operator accounts only."
      />
      <Card>
        <form onSubmit={submit} noValidate>
          <ErrorAlert error={formError} title="Sign-in failed" />
          <Field label="Operator email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              invalid={Boolean(form.formState.errors.email)}
              {...form.register("email")}
            />
          </Field>
          <Field label="Password" htmlFor="password" error={form.formState.errors.password?.message}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register("password")}
            />
          </Field>
          <Button type="submit" busy={signIn.isPending}>
            Sign in
          </Button>
        </form>
        <Alert tone="info" title="No account yet?">
          <p>
            <Link to="/sign-up">Register your company</Link> — that provisions a new tenant with
            you as its owner, on the free tier. If a colleague invited you into an existing
            company, use the link they sent you instead: signing up gives you a company of your
            own rather than a seat in theirs.
          </p>
          <p>
            A customer account from the suite portal cannot sign in to this console. Platform
            staff accounts are still created out of band — <code>make admin-seed</code> creates
            the first one.
          </p>
        </Alert>
      </Card>
    </>
  );
}
